import { type CSSProperties, type MouseEvent, type ReactNode, type Ref, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { TOUCH } from "./syncBadge";

export const HOVER_MS = 250;
export const LEAVE_MS = 150;
export const SCROLL_GRACE_MS = 150;
const EDGE = 8;
const GAP = 6;
const FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]";

let touchQuery: MediaQueryList | null = null;
const touchList = () => (touchQuery ??= matchMedia(TOUCH));
const touchWatch = (change: () => void) => {
  touchList().addEventListener("change", change);
  return () => touchList().removeEventListener("change", change);
};

/** True on a touch screen; one shared media query, so a table of hints adds no listener for each row. */
export const useTouch = () => useSyncExternalStore(touchWatch, () => touchList().matches, () => false);

/** The element that holds the floating cards: the fullscreen element when there is one, so a card shows on a fullscreen map. */
export const portalTarget = (doc: Pick<Document, "fullscreenElement" | "body"> = document): Element => doc.fullscreenElement ?? doc.body;

/** The place of a card next to its anchor, inside the viewport: below it, else above it. */
export function cardPlace(anchor: Pick<DOMRect, "left" | "top" | "width" | "bottom">, card: { width: number; height: number }, view: { width: number; height: number }) {
  const left = Math.min(Math.max(anchor.left + anchor.width / 2 - card.width / 2, EDGE), Math.max(EDGE, view.width - card.width - EDGE));
  const below = anchor.bottom + GAP;
  const above = anchor.top - GAP - card.height;
  const top = below + card.height <= view.height - EDGE || above < EDGE ? Math.min(below, Math.max(EDGE, view.height - card.height - EDGE)) : above;
  return { left, top };
}

/** The open and close of one hint: a hover opens it after 250 ms, a leave closes it after 150 ms, a tap or a focus at once. */
export function hintControl(set: (open: boolean) => void) {
  let open = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const show = (next: boolean) => {
    clearTimeout(timer);
    open = next;
    set(next);
  };
  return {
    show,
    close: () => show(false),
    tap: () => show(!open),
    hover() {
      clearTimeout(timer);
      if (!open) timer = setTimeout(() => show(true), HOVER_MS);
    },
    leave() {
      clearTimeout(timer);
      if (open) timer = setTimeout(() => show(false), LEAVE_MS);
    },
    stay: () => clearTimeout(timer),
    key(e: Pick<KeyboardEvent, "key">) {
      if (e.key === "Escape" && open) show(false);
    },
    get open() {
      return open;
    },
  };
}

type Control = ReturnType<typeof hintControl>;

/** The card of a hint, without its portal. */
export function HintCard({ id, text, touch, style, onStay, onLeave, cardRef }: { id: string; text: ReactNode; touch: boolean; style?: CSSProperties; onStay?: () => void; onLeave?: () => void; cardRef?: Ref<HTMLDivElement> }) {
  return (
    <div ref={cardRef} id={id} className={touch ? "hint-card touch" : "hint-card"} role="tooltip" data-testid="hint-card" style={style} onMouseEnter={onStay} onMouseLeave={onLeave}>
      {text}
    </div>
  );
}

function HintPopover({ id, text, anchor, touch, control }: { id: string; text: ReactNode; anchor: HTMLElement; touch: boolean; control: Control }) {
  const card = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  const [target] = useState(portalTarget);
  useLayoutEffect(() => {
    if (card.current) setPlace(cardPlace(anchor.getBoundingClientRect(), card.current.getBoundingClientRect(), { width: innerWidth, height: innerHeight }));
  }, [anchor, text]);
  useEffect(() => {
    const opened = performance.now();
    const away = (e: PointerEvent) => {
      const at = e.target as Node;
      if (!card.current?.contains(at) && !anchor.contains(at)) control.close();
    };
    const scroll = () => performance.now() - opened >= SCROLL_GRACE_MS && control.close();
    document.addEventListener("keydown", control.key);
    document.addEventListener("pointerdown", away);
    document.addEventListener("fullscreenchange", control.close);
    addEventListener("scroll", scroll, true);
    return () => {
      document.removeEventListener("keydown", control.key);
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("fullscreenchange", control.close);
      removeEventListener("scroll", scroll, true);
    };
  }, [anchor, control]);
  return createPortal(
    <HintCard id={id} text={text} touch={touch} cardRef={card} style={place ?? { visibility: "hidden" }} onStay={control.stay} onLeave={control.leave} />,
    target,
  );
}

/**
 * Hover help on a label, a chip or a value (rule U-28): a hover or a keyboard focus on a desktop, a tap on a touch screen.
 * Set `stop={false}` when the child is a button or a link, or in a long table, so the hint adds no tab stop.
 */
export function Hint({
  text,
  children,
  className,
  testId,
  stop = true,
  focusOnly = false,
}: {
  text?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
  stop?: boolean;
  /** Only a keyboard focus of the link inside opens it: a row card that adds no tab stop and no hover. */
  focusOnly?: boolean;
}) {
  const touch = useTouch();
  const [open, setOpen] = useState(false);
  const [control] = useState(() => hintControl(setOpen));
  const anchor = useRef<HTMLSpanElement>(null);
  const id = useId();
  useEffect(() => control.stay, [control]);
  useEffect(() => {
    const box = anchor.current;
    if (!box || !open) return;
    const described = box.querySelector<HTMLElement>(FOCUSABLE) ?? box;
    const others = () => (described.getAttribute("aria-describedby") ?? "").split(" ").filter((other) => other && other !== id);
    described.setAttribute("aria-describedby", [...others(), id].join(" "));
    return () => {
      const kept = others();
      if (kept.length) described.setAttribute("aria-describedby", kept.join(" "));
      else described.removeAttribute("aria-describedby");
    };
  }, [open, id]);
  if (!text) return <>{children}</>;
  const tap = (e: MouseEvent) => {
    if (!touch) return;
    if (!anchor.current?.querySelector(FOCUSABLE)) {
      if ((e.target as Element).closest?.("a[href]")) e.preventDefault();
      e.stopPropagation();
    }
    control.tap();
  };
  return (
    <>
      <span
        ref={anchor}
        className={className ? `hint-anchor ${className}` : "hint-anchor"}
        data-testid={testId}
        tabIndex={stop ? 0 : undefined}
        onMouseEnter={touch || focusOnly ? undefined : control.hover}
        onMouseLeave={touch || focusOnly ? undefined : control.leave}
        onFocus={(e) => !touch && (e.target as Element).matches(":focus-visible") && control.show(true)}
        onBlur={touch ? undefined : control.close}
        onClick={focusOnly ? undefined : tap}
      >
        {children}
      </span>
      {open && anchor.current && <HintPopover id={id} text={text} anchor={anchor.current} touch={touch} control={control} />}
    </>
  );
}

/** A label with hover help and a dotted underline, so the user can see that it has help. */
export function HelpText({ text, children, testId }: { text?: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <Hint text={text} className="help-text" testId={testId}>
      {children}
    </Hint>
  );
}

/** The label of a cell on a phone card, with the help of its column header; the cell needs `data-help` (rule U-28). */
export function CardLabel({ column, label }: { column?: { label: string; help?: ReactNode }; label?: string }) {
  return (
    <span className="desk-hide card-label">
      <Hint text={column?.help} className="help-text" stop={false}>
        {label ?? column?.label}
      </Hint>
      {": "}
    </span>
  );
}
